import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PawPrint, AlertCircle, CheckCircle, MapPin, Calendar, Phone, Mail, ArrowLeft } from 'lucide-react';

const LostAndFoundPets = () => {
  const navigate = useNavigate();
  const [lostPets, setLostPets] = useState([]);
  const [foundPets, setFoundPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactDialog, setContactDialog] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [contactForm, setContactForm] = useState({
    sender_name: '',
    sender_contact: '',
    message: ''
  });

  useEffect(() => {
    fetchLostPets();
    fetchFoundPets();
  }, []);

  const fetchLostPets = async () => {
    try {
      const response = await axios.get('/lost-pets');
      setLostPets(response.data);
    } catch (error) {
      console.error('Failed to fetch lost pets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFoundPets = async () => {
    try {
      const response = await axios.get('/found-pets');
      setFoundPets(response.data);
    } catch (error) {
      console.error('Failed to fetch found pets:', error);
    }
  };

  const handleContactOwner = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/contact-owner', {
        microchip_number: selectedPet.microchip_number,
        ...contactForm
      });
      toast.success('Message sent to owner');
      setContactDialog(false);
      setContactForm({ sender_name: '', sender_contact: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="bg-white border-b border-stone-100">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-accent" />
            <span className="text-xl font-bold">Lost & Found Pets</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Help Reunite Pets with Their Families</h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">Search our database of lost and found pets. Together, we can bring them home.</p>
        </div>

        <Tabs defaultValue="lost" className="space-y-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="lost">
              <AlertCircle className="w-4 h-4 mr-2" />
              Lost Pets ({lostPets.length})
            </TabsTrigger>
            <TabsTrigger value="found">
              <CheckCircle className="w-4 h-4 mr-2" />
              Found Pets ({foundPets.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lost">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : lostPets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-stone-600">
                  <PawPrint className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p>No lost pets reported. Let's keep it that way!</p>
                </CardContent>
              </Card>
            ) : (
              <div data-testid="lost-pets-list" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {lostPets.map((item) => (
                  <Card key={item.report.report_id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">{item.pet.name}</CardTitle>
                          <Badge className="mt-2 bg-red-100 text-red-800">Lost</Badge>
                        </div>
                        <PawPrint className="w-8 h-8 text-stone-300" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-stone-600">Species & Breed</p>
                        <p>{item.pet.species} {item.pet.breed && `- ${item.pet.breed}`}</p>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-stone-400 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-stone-600">Last Seen</p>
                          <p className="text-sm">{item.report.last_seen_location}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-stone-400 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-stone-600">Date</p>
                          <p className="text-sm">{new Date(item.report.last_seen_date).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {item.report.description && (
                        <div>
                          <p className="text-sm font-semibold text-stone-600">Description</p>
                          <p className="text-sm text-stone-600">{item.report.description}</p>
                        </div>
                      )}

                      {item.report.reward && (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-sm font-bold text-green-800">Reward: ${item.report.reward}</p>
                        </div>
                      )}

                      {item.owner?.contact_available && !item.owner?.email ? (
                        <Button 
                          data-testid="contact-owner-btn"
                          onClick={() => {
                            setSelectedPet({ ...item.pet, microchip_number: item.pet.microchip_number });
                            setContactDialog(true);
                          }}
                          className="w-full bg-accent hover:bg-accent/90"
                        >
                          Contact Owner
                        </Button>
                      ) : item.owner?.email && (
                        <div className="space-y-2 pt-3 border-t">
                          <p className="text-sm font-semibold">Owner Contact:</p>
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${item.owner.email}`} className="text-primary hover:underline">{item.owner.email}</a>
                          </div>
                          {item.owner.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4" />
                              <a href={`tel:${item.owner.phone}`} className="text-primary hover:underline">{item.owner.phone}</a>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="found">
            {foundPets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-stone-600">
                  <PawPrint className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p>No found pets waiting to be claimed.</p>
                </CardContent>
              </Card>
            ) : (
              <div data-testid="found-pets-list" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {foundPets.map((item) => (
                  <Card key={item.report.found_id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">{item.report.species}</CardTitle>
                          <Badge className="mt-2 bg-green-100 text-green-800">Found</Badge>
                        </div>
                        <PawPrint className="w-8 h-8 text-stone-300" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-stone-400 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-stone-600">Found Location</p>
                          <p className="text-sm">{item.report.found_location}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-stone-400 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-stone-600">Found Date</p>
                          <p className="text-sm">{new Date(item.report.found_date).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {item.report.microchip_number && (
                        <div className="p-3 bg-primary/5 rounded-lg">
                          <p className="text-sm font-semibold text-stone-600">Microchip</p>
                          <p className="text-sm font-mono">{item.report.microchip_number}</p>
                        </div>
                      )}

                      {item.report.description && (
                        <div>
                          <p className="text-sm font-semibold text-stone-600">Description</p>
                          <p className="text-sm text-stone-600">{item.report.description}</p>
                        </div>
                      )}

                      <div className="pt-3 border-t">
                        <p className="text-sm font-semibold mb-2">Reported By:</p>
                        <p className="text-sm">{item.reporter.name} ({item.reporter.role})</p>
                        <div className="flex items-center gap-2 text-sm mt-1">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${item.reporter.email}`} className="text-primary hover:underline">{item.reporter.email}</a>
                        </div>
                        {item.reporter.phone && (
                          <div className="flex items-center gap-2 text-sm mt-1">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${item.reporter.phone}`} className="text-primary hover:underline">{item.reporter.phone}</a>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Contact Owner Dialog */}
      <Dialog open={contactDialog} onOpenChange={setContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contact Pet Owner</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactOwner} className="space-y-4">
            <div>
              <Label>Your Name</Label>
              <Input
                data-testid="contact-name-input"
                value={contactForm.sender_name}
                onChange={(e) => setContactForm({ ...contactForm, sender_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Your Contact (Email or Phone)</Label>
              <Input
                data-testid="contact-info-input"
                value={contactForm.sender_contact}
                onChange={(e) => setContactForm({ ...contactForm, sender_contact: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                data-testid="contact-message-input"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder="I found your pet at..."
                rows={4}
                required
              />
            </div>
            <Button data-testid="send-message-btn" type="submit" className="w-full bg-primary hover:bg-primary/90">
              Send Message
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LostAndFoundPets;
